/**
 * Address Card Component
 *
 * Displays a single address with all details and action buttons
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import type { ContactAddressWithDetails } from '../../core/models';
import { CountryService } from '../../core/services/country.service';
import { CategoryDefinitionService } from '../../core/services/category-definition.service';
import { TranslatePipe } from '../../core/features/translations';

@Component({
  selector: 'app-address-card',
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="bg-white border rounded-lg p-4 mb-4 transition-all duration-200 hover:shadow-md"
         [class.border-green-500.border-2]="address().is_primary_address"
         [class.border-gray-200]="!address().is_primary_address">
      <!-- Address Header -->
      <div class="flex justify-between items-start mb-3 pb-3 border-b border-gray-100">
        <div class="flex flex-wrap gap-2 items-center">
          @if (address().is_primary_address) {
            <span class="inline-block px-2 py-1 bg-green-500 text-white rounded text-[0.75rem] font-medium leading-none">{{
              'portal.mydata.addresses.card.primary' | translate
            }}</span>
          } @else {
            <span class="inline-block px-3 py-1 bg-blue-600 text-white rounded-full text-[0.875rem] font-medium leading-none">{{ getCategoryTranslationKey() | translate }}</span>
          }
        </div>
        <div class="flex gap-2">
          @if (!isEditHidden()) {
            <button
              type="button"
              class="flex items-center justify-center w-9 h-9 border border-gray-200 bg-white rounded cursor-pointer transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              (click)="onEdit()"
              [attr.aria-label]="'portal.mydata.addresses.card.editLabel' | translate"
            >
              <span class="text-[1.2rem]">✏️</span>
            </button>
          }
          @if (!address().is_primary_address) {
            <button
              type="button"
              class="flex items-center justify-center w-9 h-9 border border-gray-200 bg-white rounded cursor-pointer transition-all duration-200 hover:bg-red-50 hover:border-red-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              (click)="onDelete()"
              [attr.aria-label]="'portal.mydata.addresses.card.deleteLabel' | translate"
            >
              <span class="text-[1.2rem]">🗑️</span>
            </button>
          }
        </div>
      </div>

      <!-- Address Content -->
      <div class="flex flex-col gap-1">
        @if (address().companyname) {
          <div class="text-gray-900 text-[0.95rem] font-bold leading-relaxed">
            {{ address().companyname }}
          </div>
        }
        @if (address().salutation || address().prename || address().lastname) {
          <div class="text-gray-900 text-[0.95rem] leading-relaxed">
            {{ address().salutation || '' }}
            {{ address().prename || '' }}
            {{ address().lastname || '' }}
          </div>
        }
        @if (address().street) {
          <div class="text-gray-900 text-[0.95rem] leading-relaxed">{{ address().street }}</div>
        }
        @if (address().zip || address().town) {
          <div class="text-gray-900 text-[0.95rem] leading-relaxed">{{ address().zip || '' }} {{ address().town || '' }}</div>
        }
        @if (address().region) {
          <div class="text-gray-900 text-[0.95rem] leading-relaxed">{{ address().region }}</div>
        }
        @if (address().fk_country_id) {
          <div class="text-gray-900 text-[0.95rem] leading-relaxed">{{ getCountryName() }}</div>
        }
      </div>
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressCardComponent {
  private readonly countryService = inject(CountryService);
  private readonly categoryService = inject(CategoryDefinitionService);

  // Inputs
  readonly address = input.required<ContactAddressWithDetails>();
  readonly disablePrimaryEdit = input<boolean>(false);

  // Outputs
  readonly edit = output<ContactAddressWithDetails>();
  readonly delete = output<ContactAddressWithDetails>();

  // Computed values
  readonly isEditHidden = computed(() => {
    return this.address().is_primary_address && this.disablePrimaryEdit();
  });
  readonly categoryName = computed(() => {
    const categoryId = this.address().fk_category_id;
    return this.categoryService.getCategoryNameById(categoryId);
  });

  readonly countryName = computed(() => {
    const countryId = this.address().fk_country_id || null;
    return this.countryService.getCountryNameById(countryId);
  });

  /**
   * Generate translation key based on category name
   * Falls back to displaying the original category name if no translation exists
   */
  getCategoryTranslationKey(): string {
    const categoryName = this.categoryName();

    if (!categoryName || categoryName === '-') {
      return 'portal.mydata.addresses.category.unknown';
    }

    // Convert category name to translation key
    // e.g., "Invoicing" -> "portal.mydata.addresses.category.invoicing"
    const normalizedName = categoryName
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/[^a-z0-9_äöüß]/g, ''); // Remove special characters except umlauts

    return `portal.mydata.addresses.category.${normalizedName}`;
  }

  getCategoryName(): string {
    return this.categoryName();
  }

  getCountryName(): string {
    return this.countryName();
  }

  onEdit(): void {
    this.edit.emit(this.address());
  }

  onDelete(): void {
    this.delete.emit(this.address());
  }
}
